import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { PayService } from '../service/pay.service';
import { DeviceChallengeResponse, JwtResponse, LoginPayload } from '../service/auth.service';
import { UserSessionService } from '../user-session.service';

type LoginForm = {
  username: FormControl<string>;
  password: FormControl<string>;
};

const MICROFINANCE_ROLES = [
  'MICROFINANCE',
  'MICROFINANCE_SUPERVISOR',
  'MICROFINANCE_CAISSE',
  'MICROFINANCE_COLLECTE',
  'MICROFINANCE_CREDIT'
];

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: false
})
export class LoginComponent implements OnInit {
  userLogin!: FormGroup<LoginForm>;
  problemAcure = false;
  public loggedIn = false;
  mySubscription: any;
  loader = false;

  readonly googlePlayLink: string;
  readonly appStoreLink: string;
  show = false;
  validatingDevice = false;
  deviceChallenge: DeviceChallengeResponse | null = null;
  deviceChallengeError = '';
  pendingUsername = '';
  readonly deviceOtp = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern('^[0-9]{6}$')]
  });

  constructor(private userService: PayService, private router: Router, private userSessionService: UserSessionService) { }

  ngOnInit() {
    this.ngOnlogin();
  }

  ngOnlogin() {
    this.userLogin = new FormGroup<LoginForm>({
      username: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          // email ou numéro camerounais (mêmes patterns que le backend utilise ailleurs)
          Validators.pattern('[^ @]*@[^ @]*|((^(65)[0-9]{1}[0-9]{6}|^(69|66|67|64)[0-9]{7}))$')
        ]
      }),
      password: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(6), Validators.maxLength(64)]
      })
    });
  }





  onlogin(formData?: LoginPayload) {
    if (this.userLogin.invalid) {
      this.userLogin.markAllAsTouched();
      return;
    }

    const payload: LoginPayload = formData ?? this.userLogin.getRawValue();
    const trimmed = (payload.username || '').trim();
    payload.username = trimmed.includes('@') ? trimmed.toLowerCase() : trimmed;
    this.pendingUsername = payload.username;

    this.loader = true;
    this.userService.login(payload).pipe(take(1)).subscribe(res => {
      if (this.isDeviceChallengeResponse(res)) {
        this.deviceChallenge = res;
        this.deviceChallengeError = '';
        this.deviceOtp.reset('');
        this.loader = false;
        return;
      }
      this.completeAuthenticatedSession(res as JwtResponse, payload.username);
      this.loader = false;
    },
      _err => {
        this.problemAcure = true;
        setTimeout(() => {
          this.loader = false;
          this.problemAcure = false;
        }, 4000);
      });
  }

  showPassword() {
    this.show = !this.show;
  }

  submitDeviceValidation(): void {
    if (!this.deviceChallenge || !this.pendingUsername) {
      return;
    }
    if (this.deviceOtp.invalid) {
      this.deviceOtp.markAsTouched();
      return;
    }

    this.validatingDevice = true;
    this.deviceChallengeError = '';
    this.userService.verifyTrustedDevice({
      username: this.pendingUsername,
      challengeToken: this.deviceChallenge.challengeToken,
      otp: this.deviceOtp.value
    }).pipe(take(1)).subscribe(res => {
      this.completeAuthenticatedSession(res, this.pendingUsername);
      this.closeDeviceChallenge();
      this.validatingDevice = false;
    }, err => {
      this.validatingDevice = false;
      this.deviceChallengeError = this.resolveDeviceChallengeError(err);
    });
  }

  closeDeviceChallenge(): void {
    this.deviceChallenge = null;
    this.deviceOtp.reset('');
    this.deviceChallengeError = '';
  }

  get challengeExpiryLabel(): string {
    const isoDate = this.deviceChallenge?.expiresAt;
    if (!isoDate) {
      return '';
    }
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString('fr-FR');
  }

  private completeAuthenticatedSession(response: JwtResponse, fallbackUsername: string): void {
    this.userService.saveConnectedUser(response);

    if (response.refreshToken) {
      localStorage.setItem('refreshToken', response.refreshToken);
    }
    const user = this.userSessionService.getUser();
    this.userService.saveUsername(user?.username || fallbackUsername);

    const roles = user?.roles ?? [];
    const isMicrofinanceUser = roles.some(role => MICROFINANCE_ROLES.includes(role));
    const route = isMicrofinanceUser && user?.partnerActivationRequired
      ? '/utilisateur/partenaire/activation'
      : this.userSessionService.getLandingRoute(roles);
    this.router.navigateByUrl(route);
  }

  private isDeviceChallengeResponse(payload: any): payload is DeviceChallengeResponse {
    return !!payload && payload.deviceValidationRequired === true && !!payload.challengeToken;
  }

  private resolveDeviceChallengeError(err: HttpErrorResponse): string {
    if (typeof err?.error === 'string' && err.error.trim()) {
      return err.error;
    }
    switch (err?.status) {
      case 400:
        return 'Code OTP invalide.';
      case 404:
        return 'Challenge introuvable. Reconnectez-vous.';
      case 409:
        return 'Challenge déjà utilisé. Reconnectez-vous.';
      case 410:
        return 'Challenge expiré. Reconnectez-vous.';
      case 423:
        return 'Trop de tentatives. Reconnectez-vous.';
      default:
        return 'Validation impossible pour le moment.';
    }
  }

}
